import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactElement,
  type ReactNode,
} from 'react';

import { api, onSessionExpired, SessionExpired, type SessionUser } from '@/api/client';

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous' | 'error';

export interface SessionValue {
  status: SessionStatus;
  user: SessionUser | null;
  /** Non-null only when the session could not be read for a reason that is not a 401. */
  error: Error | null;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

export const SessionContext = createContext<SessionValue | null>(null);

export const SESSION_QUERY_KEY = ['session'] as const;

export function SessionProvider({ children }: { children: ReactNode }): ReactElement {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: () => api.auth.me(),
    // A 401 here is an answer, not a failure: it means "nobody is signed in".
    retry: false,
  });

  const refresh = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
  }, [queryClient]);

  // Any call can be the one that discovers the cookie has expired, so the
  // shell returns to sign-in from wherever the user happened to be.
  //
  // Guarded on currently believing we are signed in. Without it the session
  // probe feeds itself: /api/auth/me answers 401, that fires this listener,
  // which refetches /api/auth/me, which answers 401. The first version of this
  // took the test worker down with it.
  const authenticated = useRef(false);
  useEffect(
    () =>
      onSessionExpired(() => {
        if (!authenticated.current) return;
        authenticated.current = false;
        void refresh();
      }),
    [refresh],
  );

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await api.auth.logout();
    } finally {
      await refresh();
    }
  }, [refresh]);

  const value = useMemo<SessionValue>(() => {
    // Error before data, deliberately. react-query keeps the last successful
    // data when a refetch fails, so checking data first would report a signed
    // out user as still signed in for as long as the cache held them.
    const expired = query.error instanceof SessionExpired;
    const status: SessionStatus = query.isPending
      ? 'loading'
      : expired
        ? 'anonymous'
        : query.error
          ? 'error'
          : 'authenticated';
    authenticated.current = status === 'authenticated';
    return {
      status,
      user: query.data?.user ?? null,
      error: status === 'error' ? query.error : null,
      refresh,
      signOut,
    };
  }, [query.isPending, query.data, query.error, refresh, signOut]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
